import { useCallback, useEffect, useState } from "react";
import { ProviderPicker } from "../components/transcript/ProviderPicker.js";
import { Banner } from "../components/ui/Banner.js";
import { Button } from "../components/ui/Button.js";
import { Card } from "../components/ui/Card.js";
import { ConfirmDialog } from "../components/ui/ConfirmDialog.js";
import { Field, Input } from "../components/ui/Field.js";
import { type OpcjaSelect, Select } from "../components/ui/Select.js";
import { useToast } from "../components/ui/Toast.js";
import { StanBledu, StanLadowania, StanPusty } from "../components/ui/stany.js";
import { api, ApiError, type ProviderLists, type SettingsDto, type TextModelDto } from "../lib/api.js";

/** Lustro walidacji z src/routes/settingsApi.ts. Rozjazd lapie tests/unit/settingsPatch.test.ts. */
const MIC_MIN = 3;
const MIC_MAX = 60;
const SOCZEWKI_MIN = 1;
const SOCZEWKI_MAX = 8;

const RETENCJE: OpcjaSelect[] = [
  { wartosc: "DELETE_AFTER_ANALYSIS", etykieta: "Skasować od razu po analizie" },
  { wartosc: "KEEP_30D", etykieta: "Zostawić na 30 dni" },
  { wartosc: "KEEP", etykieta: "Zostawić" },
];

/**
 * Ustawienia domyslnych modeli.
 *
 * To sa wartosci startowe dla NOWYCH analiz. Kazda analiza zapamietuje, czym
 * zostala policzona, wiec zmiana tutaj nie przepisuje historii.
 *
 * Widok celowo nie ma wlasnego pliku CSS (P-5): po zbudowaniu prymitywow nie
 * potrzebuje ani jednej wlasnej reguly.
 */
export function SettingsPage(): React.JSX.Element {
  const toast = useToast();

  const [settings, setSettings] = useState<SettingsDto | null>(null);
  const [providers, setProviders] = useState<ProviderLists | null>(null);
  /** Opis modelu oceny z serwera (domyslny, podpowiedzi, dostepnosc). null: starszy serwer go nie zwraca. */
  const [modelOceny, setModelOceny] = useState<TextModelDto | null>(null);
  const [bladWczytania, setBladWczytania] = useState<string | null>(null);
  /** BN-1: trwajacy STAN - kontrolka pokazuje wartosc, ktorej nie ma w bazie. */
  const [bladZapisu, setBladZapisu] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** D-1: otwarcie dostepu do transkryptow idzie przez dialog, nigdy przez window.confirm. */
  const [pytamODiagnostyke, setPytamODiagnostyke] = useState(false);
  const [przeladowanieTrwa, setPrzeladowanieTrwa] = useState(false);
  /** Wynik ostatniego przeladowania. null znaczy "jeszcze nie klikano w tej sesji". */
  const [metodyka, setMetodyka] = useState<{
    wersja: string;
    emocje: string[];
    soczewki?: { slug: string; title: string; enabled: boolean }[];
  } | null>(null);

  const wczytaj = useCallback(async () => {
    setBladWczytania(null);
    try {
      const data = await api.settings();
      setSettings(data.settings);
      setProviders(data.providers);
      setModelOceny(data.textModel ?? null);
      // Odczyt z serwera domyka stan rozjazdu: to, co widac, znowu jest tym,
      // co jest w bazie.
      setBladZapisu(null);
    } catch (err) {
      setBladWczytania(err instanceof ApiError ? `${err.status} ${err.code}` : "brak połączenia z serwerem");
    }
  }, []);

  useEffect(() => {
    void wczytaj();
  }, [wczytaj]);

  const save = async (patch: {
    sttProvider?: string;
    expressionProvider?: string;
    serProvider?: string;
    sttModel?: string | null;
    expressionModel?: string | null;
    serModel?: string | null;
    textModel?: string | null;
    defaultAudioRetention?: string;
    micMaxSeconds?: number;
    lensConcurrency?: number;
    devToolsEnabled?: boolean;
  }): Promise<void> => {
    setBusy(true);
    try {
      const data = await api.saveSettings(patch);
      setSettings(data.settings);
      setProviders(data.providers);
      setModelOceny(data.textModel ?? null);
      setBladZapisu(null);
      // TO-1: sukces zapisu to ZDARZENIE, wiec toast. Wczesniej byl tu napis
      // gasnacy po 2 s - komunikat, ktory znika, zanim zdazy zostac przeczytany.
      toast.sukces("Zapisano ustawienia.");
    } catch (err) {
      // BN-2: to nie zdarzenie, tylko stan - na ekranie stoi wybor, ktorego baza
      // nie zna, i bedzie tak stal, dopoki ktos tego nie naprawi.
      setBladZapisu(err instanceof ApiError ? `${err.status} ${err.code}` : "brak połączenia z serwerem");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Przeladowanie metodyki ma WLASNY wskaznik zajetosci, nie wspolny `busy`.
   *
   * `busy` blokuje kontrolki dostawcow, bo one czekaja na zapis do bazy. To
   * jest inna operacja, na innym zasobie, i zablokowanie nia calego panelu
   * sugerowaloby zaleznosc, ktorej nie ma.
   */
  const przeladujMetodyke = async (): Promise<void> => {
    setPrzeladowanieTrwa(true);
    try {
      const wynik = await api.przeladujMetodyke();
      setMetodyka(wynik);
      toast.sukces(`Przeładowano metodykę (wersja ${wynik.wersja}).`);
    } catch (err) {
      // Zdarzenie, nie stan: metodyka na serwerze sie NIE zmienila, wiec na
      // ekranie nie ma nic rozjechanego, co trzeba by naprawic. Powod z serwera
      // niesie nazwe pliku i dlatego idzie na ekran w calosci.
      toast.blad(
        err instanceof ApiError
          ? `Nie przeładowano metodyki: ${err.code}`
          : "Nie przeładowano metodyki - brak połączenia z serwerem.",
      );
    } finally {
      setPrzeladowanieTrwa(false);
    }
  };

  const brakModeli = providers !== null && providers.stt.length === 0;

  return (
    <>
      <h1 className="page-title">Ustawienia</h1>
      <p className="page-lead">
        Domyślne modele dla nowych analiz. Przy każdym wgrywaniu nagrania można wybrać inny, nie zmieniając
        tego, co tutaj.
      </p>

      {/* BN-2: pelna szerokosc kolumny tresci, NAD kartami. BN-3: jedyny baner widoku. */}
      {bladZapisu !== null && (
        <Banner
          wariant="danger"
          tekst="Nie udało się zapisać ustawień. Na ekranie widać wybór, którego nie ma jeszcze w bazie."
          akcja={
            <Button wariant="secondary" onClick={() => void wczytaj()}>
              Przywróć zapisane
            </Button>
          }
        />
      )}

      <Card tytul="Modele domyślne">
        {/* S-5: cztery stany renderuja sie W TEJ KARCIE. Wczesniej caly widok
            wychodzil przed czasem i przy bledzie nie bylo ani karty, ani tytulu. */}
        {bladWczytania !== null ? (
          <StanBledu
            tytul="Nie udało się wczytać ustawień"
            opis="Serwer nie odpowiedział. Spróbuj ponownie za chwilę."
            szczegol={bladWczytania}
            akcja={
              <Button wariant="secondary" onClick={() => void wczytaj()}>
                Spróbuj ponownie
              </Button>
            }
          />
        ) : settings === null || providers === null ? (
          <StanLadowania tekst="Wczytuję ustawienia..." />
        ) : brakModeli ? (
          <StanPusty
            tytul="Nie ma skonfigurowanego modelu transkrypcji"
            opis="Dodaj klucz dostawcy w konfiguracji serwera, a wybór pojawi się tutaj."
          />
        ) : (
          <>
            <ProviderPicker
              legend="Transkrypcja"
              name="settings-stt"
              providers={providers.stt}
              value={settings.sttProvider}
              onChange={(id) => void save({ sttProvider: id, sttModel: null })}
              model={settings.sttModel ?? ""}
              onModelChange={(m) => void save({ sttModel: m || null })}
              disabled={busy}
            />

            {/*
              OCENA BRZMIENIA = WYLACZNIE MODELE SYGNALOWE.
              Zmierzone w docs/eksperyment-przeciek-tresci.md: modele
              audio-jezykowe slysza slowa w samym nagraniu i opisuja TRESC
              zamiast brzmienia - na klipach sprzecznych gpt-audio i Qwen
              pomylily sie 6 razy na 6. Trzymanie ich w sekcji o brzmieniu
              bylo obietnica, ktorej nie dowoza.
            */}
            <ProviderPicker
              legend="Ocena brzmienia"
              name="settings-ser"
              providers={providers.ser}
              value={settings.serProvider}
              onChange={(id) => void save({ serProvider: id, serModel: null })}
              model={settings.serModel ?? ""}
              onModelChange={(m) => void save({ serModel: m || null })}
              modelInfo={providers.ser[0]?.modelInfo}
              allowNone
              noneLabel="Bez oceny brzmienia"
              noneNote="Zostaje sam pomiar sygnału: tempo, ton i pauzy."
              disabled={busy}
            />
            <p className="field-hint">
              Model sygnałowy ocenia samo brzmienie i nie zna słów, więc nie ma jak pójść za
              treścią. Zwraca trzy liczby - pobudzenie, wydźwięk i dominację - a nie nazwę emocji.
              Przy wersji modelu widać jego licencję: tej bez prawa do użytku komercyjnego nie wolno
              używać w pracy dla klientów.
            </p>

            <ProviderPicker
              legend="Interpretacja modelu językowego"
              name="settings-expression"
              providers={providers.expression}
              value={settings.expressionProvider}
              onChange={(id) => void save({ expressionProvider: id, expressionModel: null })}
              model={settings.expressionModel ?? ""}
              onModelChange={(m) => void save({ expressionModel: m || null })}
              allowNone
              noneLabel="Bez interpretacji"
              noneNote="Zostaje pomiar i wymiary z sygnału, bez nazwy emocji."
              disabled={busy}
            />
            <p className="field-hint">
              Te modele rozumieją słowa i to od nich pochodzi <strong>nazwa emocji</strong> z
              taksonomii - warstwa sygnałowa jej nie daje. Ale mają zmierzoną wadę: nie podajemy im
              transkrypcji, a mimo to słyszą słowa w samym nagraniu i potrafią opisać to, co ktoś
              MÓWI o swoim sposobie mówienia, zamiast tego, jak zabrzmiał.
            </p>
          </>
        )}
      </Card>

      <Card tytul="Model oceny">
        {/* Model oceny jest OSOBNA karta, a nie czescia "Modeli domyslnych": tamta znika, gdy nie ma
            modelu transkrypcji, a ocena tekstu nie potrzebuje transkrypcji. */}
        {bladWczytania !== null || settings === null ? (
          <StanPusty
            tytul="Ustawienia modelu oceny są niedostępne"
            opis={
              bladWczytania !== null
                ? "Najpierw trzeba wczytać ustawienia - ponów próbę w karcie powyżej."
                : "Wczytuję..."
            }
          />
        ) : modelOceny === null ? (
          <StanPusty
            tytul="Nie ma informacji o modelu oceny"
            opis="Serwer nie podał listy modeli oceny. Odśwież stronę po aktualizacji serwera."
          />
        ) : (
          <>
            <p className="field-hint">
              Ten model ocenia rozmowę wg metodyki, dzieli tekst bez podpisów na wypowiedzi i proponuje nazwy
              mówców. Zmiana obowiązuje od następnej analizy.
            </p>
            {!modelOceny.available && (
              <p className="field-hint">
                Ocena jest teraz niedostępna: {modelOceny.unavailableReason ?? "brak konfiguracji modelu"}.
              </p>
            )}
            <Field
              etykieta="Model oceny"
              id="ustawienia-model-oceny"
              hint="Domyślny wystarcza w większości rozmów. Inny wybierz, gdy chcesz porównać wyniki."
              waskie
            >
              {(atrybuty) => (
                <Select
                  {...atrybuty}
                  wartosc={settings.textModel ?? ""}
                  opcje={opcjeModeluOceny(modelOceny, settings.textModel ?? null)}
                  onZmiana={(v) => void save({ textModel: v || null })}
                  placeholder="Domyślny"
                />
              )}
            </Field>
          </>
        )}
      </Card>

      <Card tytul="Nagrania">
        {/*
          S-5 chce stanu w karcie, w ktorej pojawia sie tresc - ale obie karty zasila
          JEDNO zadanie. Powtorzenie tu pelnego bledu dawaloby dwa razy ten sam
          komunikat i DWA obszary aria-live czytane po sobie, a wiec halas zamiast
          informacji (P-1, BN-3). Pelny stan z akcja ponowienia niesie karta wyzej,
          tutaj zostaje krotkie wskazanie. Uklad widoku sie nie zmienia: obie karty
          stoja zawsze, w kazdym stanie.
        */}
        {bladWczytania !== null || settings === null ? (
          <StanPusty
            tytul="Ustawienia nagrań są niedostępne"
            opis={
              bladWczytania !== null
                ? "Najpierw trzeba wczytać ustawienia - ponów próbę w karcie powyżej."
                : "Wczytuję..."
            }
          />
        ) : (
          <>
            <Field
              etykieta="Co robić z nagraniem po analizie"
              id="ustawienia-retencja"
              hint="Wartość startowa dla nowych analiz. Przy każdym wgrywaniu można wybrać inną."
              waskie
            >
              {(atrybuty) => (
                <Select
                  {...atrybuty}
                  wartosc={settings.defaultAudioRetention}
                  opcje={RETENCJE}
                  onZmiana={(v) => void save({ defaultAudioRetention: v })}
                />
              )}
            </Field>

            {/* F-13: bez natywnego steppera. F-14: kontrolka liczbowa ma swoja
                minimalna szerokosc, zeby trzycyfrowa wartosc nie chowala sie za strzalkami. */}
            {/* Pole jest NIEKONTROLOWANE (defaultValue), zeby nie walczyc z uzytkownikiem
                w trakcie pisania. Klucz zwiazany z wartoscia z serwera wymusza remount,
                gdy wartosc zmieni sie z zewnatrz - inaczej "Przywroc zapisane" odswiezaloby
                stan, a w polu zostawaloby to, co klient wpisal. */}
            <Input
              key={`mic-${settings.micMaxSeconds}`}
              etykieta="Limit nagrania w trybie mikrofonu"
              id="ustawienia-mikrofon"
              type="number"
              inputMode="numeric"
              min={MIC_MIN}
              max={MIC_MAX}
              step={1}
              waskie
              hint={`W sekundach, od ${MIC_MIN} do ${MIC_MAX}. Dłuższe próbki nie poprawiają pomiaru, a wydłużają czekanie.`}
              defaultValue={settings.micMaxSeconds}
              // Zapis na blur, nie na kazde nacisniecie klawisza: inaczej wpisanie
              // "15" wyslaloby najpierw "1", czyli wartosc spoza widelek, i pokazalo
              // blad w polowie pisania.
              onBlur={(e) => {
                const v = Number(e.currentTarget.value);
                if (!Number.isInteger(v) || v < MIC_MIN || v > MIC_MAX) {
                  e.currentTarget.value = String(settings.micMaxSeconds);
                  return;
                }
                if (v !== settings.micMaxSeconds) void save({ micMaxSeconds: v });
              }}
            />

            <Input
              key={`parametry-${settings.lensConcurrency}`}
              etykieta="Ile parametrów oceny liczyć naraz"
              id="ustawienia-parametry"
              type="number"
              inputMode="numeric"
              min={SOCZEWKI_MIN}
              max={SOCZEWKI_MAX}
              step={1}
              waskie
              hint={`Od ${SOCZEWKI_MIN} do ${SOCZEWKI_MAX}. Każdy parametr to osobne wywołanie modelu. Więcej naraz to szybsza ocena, ale i większe zużycie limitów u dostawcy modelu.`}
              defaultValue={settings.lensConcurrency}
              onBlur={(e) => {
                const v = Number(e.currentTarget.value);
                if (!Number.isInteger(v) || v < SOCZEWKI_MIN || v > SOCZEWKI_MAX) {
                  e.currentTarget.value = String(settings.lensConcurrency);
                  return;
                }
                if (v !== settings.lensConcurrency) void save({ lensConcurrency: v });
              }}
            />
          </>
        )}
      </Card>

      <Card tytul="Narzędzia diagnostyczne">
        {/* S-5: karta stoi zawsze, tak samo jak dwie powyzej. Pelny stan bledu z
            ponowieniem niesie karta "Modele domyslne" - tutaj krotkie wskazanie,
            zeby nie mnozyc obszarow aria-live (P-1, BN-3). */}
        {bladWczytania !== null || settings === null ? (
          <StanPusty
            tytul="Stan narzędzi diagnostycznych jest nieznany"
            opis={
              bladWczytania !== null
                ? "Najpierw trzeba wczytać ustawienia - ponów próbę w karcie powyżej."
                : "Wczytuję..."
            }
          />
        ) : (
          <>
            <p className="field-hint">
              Diagnostyczne API <code>/dev-tools/*</code> pozwala lokalnemu agentowi zbadać działającą
              aplikację: sprawdzić zdrowie, przejrzeć logi i kolejkę, pobrać paczkę audytu analizy. Token
              jest zapisany w repozytorium, a nie w konfiguracji serwera. Otwarcie działa przez 24 godziny
              i zamyka się samo.
            </p>
            <p className="field-hint">
              Dostęp obejmuje treść rozmów administratora (rozmowy kont użytkowników bez treści), więc
              otwieraj go tylko na czas, w którym ktoś faktycznie diagnozuje aplikację.
            </p>

            {settings.devToolsEnabled && settings.devToolsEnabledUntil !== null ? (
              <>
                <p>
                  Otwarte do{" "}
                  <strong>{new Date(settings.devToolsEnabledUntil).toLocaleString("pl-PL")}</strong>.
                </p>
                <Button
                  wariant="danger"
                  zajety={busy}
                  onClick={() => void save({ devToolsEnabled: false })}
                >
                  Zamknij dostęp teraz
                </Button>
              </>
            ) : (
              <>
                <p className="field-hint">
                  Zamknięte. Całe <code>/dev-tools/*</code> odpowiada teraz 404. Z internetu odpowiada 404
                  zawsze - diagnostyka jest osiągalna tylko z serwera albo przez tunel SSH.
                </p>
                <Button wariant="secondary" zajety={busy} onClick={() => setPytamODiagnostyke(true)}>
                  Otwórz na 24 godziny
                </Button>
              </>
            )}
          </>
        )}
      </Card>

      <Card tytul="Metodyka">
        {/* Metodyka to dane, nie kod - lista emocji i progi tempa leżą w plikach
            JSON i zmienia je konsultant, nie programista. Ten przycisk jest
            drugą połową tej obietnicy: bez niego zmiana pliku wymagała restartu
            serwera, bo metodyka jest wczytywana raz i trzymana w pamięci. */}
        <p className="field-hint">
          Zasady oceny - lista emocji, progi tempa i parametry oceny - leżą w plikach w katalogu{" "}
          <code>methodology/</code> i są wczytywane przy starcie. Na komputerze programisty po zmianie
          pliku wystarczy przeładować je tutaj. Na serwerze pliki są częścią wdrożonej aplikacji, więc
          zmiana treści wymaga nowego wdrożenia.
        </p>
        <p className="field-hint">
          Zmiana obowiązuje od następnej analizy. Ta, która właśnie trwa, dokończy się według
          metodyki, z którą się zaczęła. Gdyby plik był niepoprawny, przeładowanie odbije się
          błędem, a aplikacja zostanie przy metodyce, która działa.
        </p>

        <Button
          wariant="secondary"
          zajety={przeladowanieTrwa}
          onClick={() => void przeladujMetodyke()}
        >
          Przeładuj metodykę z dysku
        </Button>

        {/* Wynik stoi tu, a nie tylko w toascie: lista emocji jest tym, po czym
            widać, że plik faktycznie się przeładował, i warto, żeby została na
            ekranie dłużej niż kilka sekund. */}
        {metodyka !== null && (
          <p className="field-hint">
            Wczytana metodyka w wersji <strong>{metodyka.wersja}</strong>, emocji na liście:{" "}
            <strong>{metodyka.emocje.length}</strong> ({metodyka.emocje.join(", ")}).
          </p>
        )}
        {metodyka?.soczewki && metodyka.soczewki.length > 0 && (
          <p className="field-hint">
            Parametry oceny:{" "}
            <strong>
              {metodyka.soczewki.map((p) => (p.enabled ? p.title : `${p.title} (wyłączony)`)).join(", ")}
            </strong>
            .
          </p>
        )}
      </Card>

      <Card tytul="Jak czytać adnotacje">
        {/* ST-1 / ST-2: tresc czysto wyjasniajaca NIE dostaje koloru ostrzezenia.
            Wczesniej stala tu klasa .quality-note, czyli tint statusu uzyty jako
            dekoracja - status jest sygnalem, a nie sposobem wyrozniania akapitu.
            Rola "podpis" (P-4) to w tym systemie .field-hint: --text-muted, blok. */}
        <p className="field-hint">
          Adnotacja przy wypowiedzi składa się z dwóch części. Pomiar - tempo, ton, pauzy - liczony jest
          z sygnału i znaczy to samo w każdym języku. Ocena brzmienia pochodzi z modelu i jest
          interpretacją. Gdy nagranie nie daje podstaw do pomiaru, etykieta po prostu się nie pojawia -
          zamiast zgadywanej liczby.
        </p>
        {/* Dopisane po zgłoszeniu z 19.08.2026. Użytkownik ma prawo wiedzieć, że
            model bywa prowadzony przez słowa, bo inaczej bierze jego opis za
            odczyt z dźwięku. */}
        <p className="field-hint">
          Modelowi językowemu nie podajemy transkrypcji, ale słowa słyszy w samym nagraniu. Zdarza
          się przez to, że opisuje to, co ktoś MÓWI o swoim sposobie mówienia, zamiast tego, jak
          naprawdę zabrzmiał. Dlatego jego opis tempa i tonu zestawiamy z pomiarem, a gdy przeczy
          pomiarowi - odrzucamy go i mówimy o tym przy wypowiedzi.
        </p>
        {/* Dopisane 23.08.2026, razem z warstwa sygnalowa. Trzy warstwy na ekranie
            wymagaja jednego zdania o tym, ktora jest ktora - inaczej uzytkownik
            widzi trzy liczby i dwie opinie bez klucza do nich. */}
        <p className="field-hint">
          Adnotacja ma teraz trzy warstwy i różnią się tym, skąd biorą pewność.{" "}
          <strong>Pomiar</strong> liczy się z sygnału i znaczy to samo w każdym języku.{" "}
          <strong>Wymiary</strong> pochodzą z modelu, który słyszy brzmienie, ale nie zna słów.{" "}
          <strong>Nazwa emocji</strong> pochodzi z modelu, który rozumie treść - i dlatego bywa
          przez nią prowadzony.
        </p>
      </Card>

      <ConfirmDialog
        otwarty={pytamODiagnostyke}
        tytul="Otworzyć narzędzia diagnostyczne na 24 godziny?"
        tresc="Przez ten czas token z repozytorium daje dostęp do logów tej instalacji i transkryptów rozmów administratora (rozmowy kont użytkowników bez treści). Dostęp zamknie się sam, można go też zamknąć wcześniej."
        etykietaPotwierdzenia="Otwórz na 24 godziny"
        zajety={busy}
        onPotwierdz={() => {
          setPytamODiagnostyke(false);
          void save({ devToolsEnabled: true });
        }}
        onAnuluj={() => setPytamODiagnostyke(false)}
      />
    </>
  );
}

/**
 * Opcje wyboru modelu oceny: domyslny ze srodowiska serwera (pusta wartosc) i podpowiedzi z API
 * dostawcy. Zapisany model, ktorego nie ma na liscie (np. wycofany), zostaje na niej jako wybrany:
 * inaczej Select pokazalby "Domyslny" i ukryl, czym naprawde liczy sie ocena.
 */
function opcjeModeluOceny(opis: TextModelDto, wybrany: string | null): OpcjaSelect[] {
  const modele = new Set(opis.models);
  if (wybrany) modele.add(wybrany);
  return [
    { wartosc: "", etykieta: `Domyślny (${opis.model})` },
    ...[...modele].map((m) => ({ wartosc: m, etykieta: m })),
  ];
}
